import get from 'lodash/get';
import axios from 'axios';
import {fetchAndCache} from '@/utils/cached-promise';

const ProductApi = {
  /**
   * Get stock availability for the given variant.
   * @param {object} options
   * @return {*}
   */
  getStockAvailability(options) {
    const defaults = {
      productCode: '',
      isInStockOnline: true,
      retailersMax: 3,
      inStockOnly: false,
      locationText: '',
      showLocallyWidget: false,
    };

    options = Object.assign(defaults, options);
    return new Promise((resolve, reject) => {
      const headers = {'X-Requested-With': 'XMLHttpRequest'};
      let url = `/rpa/${options.productCode}?retailers=${options.retailersMax}&isInStockOnline=${Boolean(
        options.isInStockOnline
      )}&inStockOnly=${Boolean(options.inStockOnly)}`;
      if (options.locationText) {
        url = `${url}&locationText=${options.locationText}`;
      }
      if (options.showLocallyWidget) {
        url = `/rpa/${options.productCode}/locallyWidget?isInStockOnline=${Boolean(
          options.isInStockOnline
        )}&inStockOnly=${Boolean(options.inStockOnly)}`;
      }

      axios
        .get(url, {headers})
        .then((response) => {
          resolve(get(response, 'data', {}));
        })
        .catch((reason) => {
          reject(reason);
        });
    });
  },

  /**
   * Load product data by product's code.
   * @param {string} code
   * @return {Promise}
   */
  loadProduct(code) {
    const endpointUrl = `/v1/api/product/${code}/full/`;
    return new Promise((resolve) => {
      fetchAndCache(`product-${code}`, () => {
        return axios.get(endpointUrl);
      }).then((response) => {
        resolve(get(response, 'data.data', {}));
      });
    });
  },

  /**
   * Check product IDs.
   * @param {array} ids
   */
  productIdsToCommaSeparated(ids) {
    if (!ids || !ids.length) {
      throw new Error('Product IDs cannot be empty!');
    }

    return ids.join(',');
  },

  /**
   * Batch load a list of products by given list of IDs.
   * @param {array} ids
   * @return {Promise<array>}
   */
  batchLoadProducts(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/products/grid?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`products-${idsList}`, () => {
        return axios.get(endpointUrl);
      }).then((response) => {
        resolve(get(response, 'data.data', []));
      });
    });
  },

  /**
   * Load a full data of products grid by given list of IDs.
   * @param {array} ids
   * @return {Promise<object>}
   */
  loadSkuGrid(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/order-copy-paste/grid?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`products-${idsList}`, () => {
        return axios.get(endpointUrl);
      }).then((response) => {
        resolve(get(response, 'data', {}));
      });
    });
  },

  /**
   * Batch load allocated / back ordered counts for given list of product IDs.
   * @param {array} ids
   * @return {Promise<array>}
   */
  batchLoadAllocatedData(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/products/allocated-lines?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`allocated-lines-${idsList}`, () => {
        return axios.get(endpointUrl);
      }).then((response) => {
        resolve(get(response, 'data.data', []));
      });
    });
  },

  /**
+   * Batch load a list of variants by given list of sku codes.
+   * @param {array} ids
+   * @return {Promise<array>}
+   */
  fetchVariantsPrices(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/v1/api/batch/price/all/`;

    return new Promise((resolve) => {
      fetchAndCache(`products-prices-${idsList}`, () => {
        return axios.post(endpointUrl, {variantCodesList: ids});
      }).then((response) => {
        resolve(get(response, 'data.data', []));
      });
    });
  },
};

export {ProductApi};
